import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, Stack, Typography } from '@mui/joy';
import { DotsSixVertical, X } from '@phosphor-icons/react';
import { ReactNode } from 'react';

import { AttributeType } from 'companySettings/enums';
import { Button } from 'core/components';
import { Color, Direction, Level, Variant } from 'theme/enums';

import { AttributeValueChip } from './AttributeValueChip';
import { IAttributeValueField } from './types';

interface IRowProps {
  value: IAttributeValueField;
  position: number;
  type: AttributeType;
  onRemove: () => void;
  endDecorator?: ReactNode;
}

const AttributeValueRow = ({ value, position, type, onRemove, endDecorator }: IRowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: value.id,
  });

  return (
    <Stack
      ref={setNodeRef}
      direction={Direction.ROW}
      sx={{
        alignItems: 'center',
        gap: 1,
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
    >
      <Box
        {...attributes}
        {...listeners}
        sx={{ display: 'flex', color: 'neutral.500', cursor: 'grab', touchAction: 'none' }}
      >
        <DotsSixVertical weight="bold" />
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: 32,
          height: 32,
          border: '1px solid',
          borderColor: 'neutral.300',
          borderRadius: 6,
          backgroundColor: 'background.surface',
        }}
      >
        <Typography level={Level.TITLE_SM}>{position}</Typography>
      </Box>

      <AttributeValueChip value={value} type={type} />

      <Button icon={<X />} variant={Variant.PLAIN} color={Color.NEUTRAL} onClick={onRemove} />

      {endDecorator}
    </Stack>
  );
};

interface IProps {
  values: IAttributeValueField[];
  type: AttributeType;
  onMove: (fromIndex: number, toIndex: number) => void;
  onRemove: (index: number) => void;
  getEndDecorator?: (value: IAttributeValueField) => ReactNode;
}

// The list's values in their order; dragging a row by its handle reorders them. A row can carry
// something after its remove button, such as a badge.
export const AttributeValueRows = ({ values, type, onMove, onRemove, getEndDecorator }: IProps) => {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) {
      return;
    }

    onMove(
      values.findIndex((value) => value.id === active.id),
      values.findIndex((value) => value.id === over.id)
    );
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext
        items={values.map((value) => value.id)}
        strategy={verticalListSortingStrategy}
      >
        <Stack gap={1}>
          {values.map((value, index) => (
            <AttributeValueRow
              key={value.id}
              value={value}
              position={index + 1}
              type={type}
              onRemove={() => onRemove(index)}
              endDecorator={getEndDecorator?.(value)}
            />
          ))}
        </Stack>
      </SortableContext>
    </DndContext>
  );
};
